import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useCurrentUser } from '../../shared/auth';
import { DEFAULT_LOCALE } from '../../shared/i18n/locales';
import { createAstraI18n } from './i18n/createAstraI18n';

interface AstraI18nProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Supplies the react-i18next instance the shared `AgentChatPanel` translates
 * through, kept in step with the signed-in user's saved language.
 *
 * Both chat surfaces mount this: the mobile shell (via `AstraMobileProvider`)
 * and the desktop panel (inside `V2Adapter`, whose `DesktopAuthBridge` makes
 * `useCurrentUser` resolve). The rest of the app translates with arb-tools;
 * this instance is scoped to the chat subtree so the two stacks never meet.
 */
export function AstraI18nProvider({
  children,
}: AstraI18nProviderProps): React.ReactElement {
  const { userPreferences } = useCurrentUser();
  // Optional chaining on `profileSettings` too: the V1 shell's user details
  // (bridged in on desktop) type it looser than V2 does and read it with `?.`,
  // and a first-login user may have no saved profile yet.
  const locale = userPreferences?.profileSettings?.language ?? DEFAULT_LOCALE;

  // One instance per mount (lazy initializer, so the factory runs once); the
  // language is switched in place below rather than rebuilding, since a
  // rebuild would remount every translated node.
  const [i18n] = useState(() => createAstraI18n(locale));

  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
  }, [i18n, locale]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
